import * as joda from "@js-joda/core";
import * as c from "purify-ts/Codec";
import { Either, Left, Right } from "purify-ts/Either";
import { tryExtractErrorMessage } from "trader.ts/utils";
import { translateMonth } from "../translations_specific";
import { isNumber, isString } from "./istype";
import { parseIntSafe } from "./parseintsafe";
import { isEqual } from "./isequal";

export const monthC = c.Codec.custom<joda.Month>({
  encode: (t: joda.Month): string => {
    return t.toString();
  },
  decode: (u: unknown): Either<string, joda.Month> => {
    if (isString(u)) {
      try {
        const parsed = joda.Month.values().find((v) => v.toString() === u);
        if (parsed) {
          return Right(parsed);
        } else {
          return Left(`Failed to parse month: invalid value ${u}`);
        }
      } catch (err) {
        return Left("Failed to parse month: " + tryExtractErrorMessage(err));
      }
    } else {
      return Left(`Need string to decode month: ${JSON.stringify(u)}`);
    }
  },
  schema: () => ({
    type: "string",
  }),
});

export const instantC = c.Codec.custom<joda.Instant>({
  encode: (t: joda.Instant): number => {
    return t.toEpochMilli();
  },
  decode: (u: unknown): Either<string, joda.Instant> => {
    if (isString(u)) {
      try {
        const parsed = joda.Instant.parse(u);
        return Right(parsed);
      } catch (err) {
        return Left("Failed to parse instant: " + tryExtractErrorMessage(err));
      }
    }
    if (isNumber(u)) {
      try {
        return Right(joda.Instant.ofEpochMilli(u));
      } catch (err) {
        return Left(tryExtractErrorMessage(err));
      }
    } else {
      return Left(
        `Need number or string to decode instant: ${JSON.stringify(u)}`,
      );
    }
  },
  schema: () => ({
    type: "number",
  }),
});

export const weekdayC = c.Codec.custom<joda.DayOfWeek>({
  encode: (t: joda.DayOfWeek): number => {
    //return t.toString();
    return t.ordinal();
  },
  decode: (u: unknown): Either<string, joda.DayOfWeek> => {
    if (typeof u !== "number") {
      return Left(`Need number to decode weekday: ${JSON.stringify(u)}`);
    } else {
      try {
        return Right(joda.DayOfWeek.of(u + 1));
      } catch (err) {
        return Left(tryExtractErrorMessage(err));
      }
    }
  },
  schema: () => ({
    type: "number",
  }),
});

export const localTimeC = c.Codec.custom<joda.LocalTime>({
  encode: (t: joda.LocalTime): string => {
    //return t.toString();
    return t.format(joda.DateTimeFormatter.ISO_LOCAL_TIME);
  },
  decode: (u: unknown): Either<string, joda.LocalTime> => {
    if (typeof u !== "string") {
      return Left(`Need string to decode time: ${JSON.stringify(u)}`);
    } else {
      try {
        return Right(joda.LocalTime.parse(u));
      } catch (err) {
        return Left(tryExtractErrorMessage(err));
      }
    }
  },
  schema: () => ({
    type: "string",
    format: "time",
  }),
});

export const allWeekdays = [
  joda.DayOfWeek.MONDAY,
  joda.DayOfWeek.TUESDAY,
  joda.DayOfWeek.WEDNESDAY,
  joda.DayOfWeek.THURSDAY,
  joda.DayOfWeek.FRIDAY,
  joda.DayOfWeek.SATURDAY,
  joda.DayOfWeek.SUNDAY,
];

export function makeDateRange(
  d1: joda.LocalDate,
  d2: joda.LocalDate,
): joda.LocalDate[] {
  if (d2.isBefore(d1)) {
    return [];
  } else {
    return [d1, ...makeDateRange(d1.plusDays(1), d2)];
  }
}

export function isContained(
  d: joda.LocalDate,
  container: { firstday: joda.LocalDate; lastday: joda.LocalDate },
) {
  if (d.isBefore(container.firstday) || d.isAfter(container.lastday)) {
    return false;
  } else {
    return true;
  }
}

export const dateFormatWithYear =
  joda.DateTimeFormatter.ofPattern("dd/MM/yyyy");

export function dateFormatFuncddMMMM(d: joda.LocalDate) {
  return d.dayOfMonth() + " " + translateMonth(d.month());
}
export function dateFormatFuncddMMMMyyyy(d: joda.LocalDate) {
  return dateFormatFuncddMMMM(d) + " " + d.year();
}

export function getMonday(d: joda.LocalDate): joda.LocalDate {
  const dow = d.dayOfWeek();
  if (isEqual(dow, joda.DayOfWeek.MONDAY)) {
    return d;
  } else {
    return d.minusDays(dow.ordinal());
  }
}

export function getFirstWeekdayFrom(
  d: joda.LocalDate,
  reqDow: joda.DayOfWeek,
): joda.LocalDate {
  const currDow = d.dayOfWeek();
  if (reqDow.ordinal() < currDow.ordinal()) {
    return d.plusDays(reqDow.ordinal() + 7 - currDow.ordinal());
  } else {
    return d.plusDays(reqDow.ordinal() - currDow.ordinal());
  }
}

export function timeInTimerange(
  a: {
    time: joda.LocalTime;
    duration: number;
  },
  b: joda.LocalTime,
): boolean {
  return (
    isEqual(a.time, b) ||
    (a.time.isBefore(b) && a.time.plusMinutes(a.duration).isAfter(b))
  );
}

export function dateInDaterange(
  a: {
    date: joda.LocalDate;
  },
  b: { from: joda.LocalDate; until: joda.LocalDate },
): boolean {
  return (
    a.date.isEqual(b.from) ||
    a.date.isEqual(b.until) ||
    (a.date.isAfter(b.from) && a.date.isBefore(b.until))
  );
}

export function dateRangesOverlap(
  a: {
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
  },
  b: {
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
  },
): boolean {
  return (
    a.firstday.isEqual(b.firstday) ||
    a.firstday.isEqual(b.lastday) ||
    a.lastday.isEqual(b.firstday) ||
    a.lastday.isEqual(b.lastday) ||
    // overlap 'left'
    (a.firstday.isBefore(b.firstday) && a.lastday.isAfter(b.firstday)) ||
    // overlap 'right'
    (a.firstday.isBefore(b.lastday) && a.lastday.isAfter(b.lastday)) ||
    // a encompasses b
    (a.firstday.isBefore(b.firstday) && a.lastday.isAfter(b.lastday)) ||
    // b encompasses a
    (a.firstday.isAfter(b.firstday) && a.lastday.isBefore(b.lastday))
  );
}

export function timerangeInTimerange<
  A extends {
    time: joda.LocalTime;
    duration: number;
    weekday?: joda.DayOfWeek;
  },
  B extends {
    time: joda.LocalTime;
    duration: number;
    weekday?: joda.DayOfWeek;
  },
>(a: A, b: B): boolean {
  if (a.weekday && b.weekday && isEqual(a.weekday, b.weekday) === false) {
    // b is on a completely different day
    return false;
  } else {
    const aEnd = a.time.plusMinutes(a.duration);
    const bEnd = b.time.plusMinutes(b.duration);

    if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() >= aEnd.toSecondOfDay()
    ) {
      // (* b envelopes a *)
      return true;
    } else {
      return false;
    }
  }
}

// Half open range
export function timerangesOverlap<
  A extends {
    time: joda.LocalTime;
    duration: number;
    weekday?: joda.DayOfWeek;
  },
  B extends {
    time: joda.LocalTime;
    duration: number;
    weekday?: joda.DayOfWeek;
  },
>(a: A, b: B): boolean {
  if (a.weekday && b.weekday && isEqual(a.weekday, b.weekday) === false) {
    // b is on a completely different day
    return false;
  } else {
    const aEnd = a.time.plusMinutes(a.duration);
    const bEnd = b.time.plusMinutes(b.duration);

    if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() >= aEnd.toSecondOfDay()
    ) {
      // (* b envelopes a *)
      return true;
    } else if (
      b.time.toSecondOfDay() > a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() < aEnd.toSecondOfDay()
    ) {
      // (* b is inside a *)
      return true;
    } else if (
      b.time.toSecondOfDay() < a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() > a.time.toSecondOfDay()
    ) {
      // (* b overlaps a on the "left" of the timeline *)
      return true;
    } else if (
      b.time.toSecondOfDay() < aEnd.toSecondOfDay() &&
      bEnd.toSecondOfDay() > a.time.toSecondOfDay()
    ) {
      //  (* b overlaps a on the "right" of the timeline *)
      return true;
    } else {
      return false;
    }
  }
}

export function subtractTimeranges<
  A extends { time: joda.LocalTime; duration: number; weekday: joda.DayOfWeek },
  B extends { time: joda.LocalTime; duration: number; weekday: joda.DayOfWeek },
>(a: A, b: B): A[] {
  if (isEqual(a.weekday, b.weekday) === false) {
    // b is on a completely different day
    return [a];
  } else {
    const aEnd = a.time.plusMinutes(a.duration);
    const bEnd = b.time.plusMinutes(b.duration);

    if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() >= aEnd.toSecondOfDay()
    ) {
      // (* b envelopes a *)
      return [];
    } else if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() <= a.time.toSecondOfDay()
    ) {
      // (* b is completely before (or "left" on a timeline) of a *)
      return [a];
    } else if (
      b.time.toSecondOfDay() >= aEnd.toSecondOfDay() &&
      bEnd.toSecondOfDay() >= aEnd.toSecondOfDay()
    ) {
      // (* b is completely after (or "right" on a timeline) of a *)
      return [a];
    } else if (
      b.time.toSecondOfDay() > a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() < aEnd.toSecondOfDay()
    ) {
      // (* b is inside a *)
      return [
        {
          ...a,
          time: a.time,
          duration: a.time.until(b.time, joda.ChronoUnit.MINUTES),
        },
        {
          ...a,
          time: bEnd,
          duration: bEnd.until(aEnd, joda.ChronoUnit.MINUTES),
        },
      ];
    } else if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() > a.time.toSecondOfDay()
    ) {
      // (* b overlaps a on the "left" of the timeline *)
      return [
        {
          ...a,
          time: bEnd,
          duration: bEnd.until(aEnd, joda.ChronoUnit.MINUTES),
        },
      ];
    } else if (
      b.time.toSecondOfDay() < aEnd.toSecondOfDay() &&
      bEnd.toSecondOfDay() > a.time.toSecondOfDay()
    ) {
      //  (* b overlaps a on the "right" of the timeline *)
      return [
        {
          ...a,
          time: a.time,
          duration: a.time.until(b.time, joda.ChronoUnit.MINUTES),
        },
      ];
    } else {
      // (* we've covered everything *)
      return [a];
    }
  }
}

export function getTimerangesOverlap<
  A extends { time: joda.LocalTime; duration: number; weekday: joda.DayOfWeek },
  B extends { time: joda.LocalTime; duration: number; weekday: joda.DayOfWeek },
>(a: A, b: B): null | A {
  if (isEqual(a.weekday, b.weekday) === false) {
    // b is on a completely different day
    return null;
  } else {
    const aEnd = a.time.plusMinutes(a.duration);
    const bEnd = b.time.plusMinutes(b.duration);

    if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() >= aEnd.toSecondOfDay()
    ) {
      // (* b envelopes a *)
      return a;
    } else if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() <= a.time.toSecondOfDay()
    ) {
      // (* b is completely before (or "left" on a timeline) of a *)
      return null;
    } else if (
      b.time.toSecondOfDay() >= aEnd.toSecondOfDay() &&
      bEnd.toSecondOfDay() >= aEnd.toSecondOfDay()
    ) {
      // (* b is completely after (or "right" on a timeline) of a *)
      return null;
    } else if (
      b.time.toSecondOfDay() > a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() < aEnd.toSecondOfDay()
    ) {
      // (* b is inside a *)
      return {
        ...a,
        time: b.time,
        duration: b.duration,
      };
    } else if (
      b.time.toSecondOfDay() <= a.time.toSecondOfDay() &&
      bEnd.toSecondOfDay() > a.time.toSecondOfDay()
    ) {
      // (* b overlaps a on the "left" of the timeline *)
      return {
        ...a,
        time: a.time,
        duration: a.time.until(bEnd, joda.ChronoUnit.MINUTES),
      };
    } else if (
      b.time.toSecondOfDay() < aEnd.toSecondOfDay() &&
      bEnd.toSecondOfDay() > a.time.toSecondOfDay()
    ) {
      //  (* b overlaps a on the "right" of the timeline *)
      return {
        ...a,
        time: b.time,
        duration: b.time.until(aEnd, joda.ChronoUnit.MINUTES),
      };
    } else {
      // (* we've covered everything *)
      return null;
    }
  }
}

export function getMonthsFromDateRange(
  d1: joda.LocalDate,
  d2: joda.LocalDate,
): [number, joda.Month][] {
  let currDate = joda.LocalDate.of(d1.year(), d1.month(), 1);
  const res: [number, joda.Month][] = [];
  while (currDate.isBefore(d2) || currDate.isEqual(d2)) {
    res.push([currDate.year(), currDate.month()]);
    currDate = currDate.plusMonths(1);
  }
  return res;
}

export function parseWeekdayDutch(s: string): joda.DayOfWeek {
  const lower = s.trim().toLowerCase();
  switch (lower) {
    case "maandag": {
      return joda.DayOfWeek.MONDAY;
    }
    case "dinsdag": {
      return joda.DayOfWeek.TUESDAY;
    }
    case "woensdag": {
      return joda.DayOfWeek.WEDNESDAY;
    }
    case "donderdag": {
      return joda.DayOfWeek.THURSDAY;
    }
    case "vrijdag": {
      return joda.DayOfWeek.FRIDAY;
    }
    case "zaterdag": {
      return joda.DayOfWeek.SATURDAY;
    }
    case "zondag": {
      return joda.DayOfWeek.SUNDAY;
    }
  }
  throw new Error(`Failed to parse dutch weekday: ${s}`);
}

export function parseLocalTime(s_: string): joda.LocalTime {
  const s = s_.replace(".", ":");
  const split = s.split(":");
  if (split.length === 2 || split.length === 3) {
    const h = parseIntSafe(split[0]);
    const m = parseIntSafe(split[1]);
    if (h === null || m === null || h > 23 || m > 59) {
      throw new Error(`Failed to parse Time: ${s_}`);
    }
    return joda.LocalTime.of(h, m);
  } else {
    throw new Error(`Failed to parse Time: ${s_}`);
  }
}

export function calculateAge(opts: {
  birthday: joda.LocalDate;
  today: joda.LocalDate;
}): number {
  return joda.Period.between(opts.birthday, opts.today).years();
}

export function getDatesDiff(opts: {
  old: joda.LocalDate[];
  new: joda.LocalDate[];
}): { added: joda.LocalDate[]; removed: joda.LocalDate[] } {
  const added = [];
  const removed = [];
  for (const o of opts.old) {
    if (opts.new.some((n) => n.isEqual(o))) {
      // contained
    } else {
      removed.push(o);
    }
  }
  for (const n of opts.new) {
    if (opts.old.some((o) => n.isEqual(o))) {
      // contained
    } else {
      added.push(n);
    }
  }

  return { added, removed };
}

export function dateRangesAreEqual(
  hs1: {
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
  }[],
  hs2: {
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
  }[],
): boolean {
  const dates1 = hs1
    .flatMap((h) => makeDateRange(h.firstday, h.lastday))
    .uniqBy((d) => d.toEpochDay())
    .orderBy((d) => d.toEpochDay());
  const dates2 = hs2
    .flatMap((h) => makeDateRange(h.firstday, h.lastday))
    .uniqBy((d) => d.toEpochDay())
    .orderBy((d) => d.toEpochDay());
  if (dates1.length !== dates2.length) {
    return false;
  }
  let i = 0;
  for (const d1 of dates1) {
    const d2 = dates2[i];
    if (!d1.isEqual(d2)) {
      return false;
    }
    i++;
  }
  return true;
}
